import { useCallback, useRef, useState } from "react";
import { documentUploadService } from "../service/Documentuploadservice";

export interface UploadedDocument {
  fid: string;
  filename: string;
  filesize: number;
  url: string;
}

export const DocumentUploadViewModel = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocument[]
  >([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const onUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const loadExistingDocuments = useCallback(
    (documents: UploadedDocument[]) => {
      // console.log("ViewModel: Loading existing documents from prop:", documents);
      setUploadedDocuments(documents);
    },
    []
  );

  const onFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate type - only DOC and PDF
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(file.type)) {
        setError("Please select a DOC or PDF file");
        return;
      }

      // Validate size - max 3MB
      const maxSizeBytes = 3 * 1024 * 1024; // 3MB
      if (file.size > maxSizeBytes) {
        setError("File must be smaller than 3MB");
        return;
      }

      setError(null);
      setIsUploading(true);

      try {
        // Upload file to get fid and URI
        const uploadResponse = await documentUploadService.uploadFile(file);

        // console.log("ViewModel: Upload response:", uploadResponse);

        // Extract FID
        const fid = uploadResponse.fid?.[0]?.value?.toString();

        if (!fid) {
          throw new Error("No FID received from server");
        }

        // Extract file info
        const filename = uploadResponse.filename?.[0]?.value || file.name;
        const filesize = uploadResponse.filesize?.[0]?.value || file.size;

        let url: string = "";
        if (
          uploadResponse.uri &&
          Array.isArray(uploadResponse.uri) &&
          uploadResponse.uri[0]
        ) {
          const uriItem = uploadResponse.uri[0];
          url = uriItem.url || uriItem.value;
        }

        // Add to uploaded documents list
        const newDocument: UploadedDocument = {
          fid,
          filename,
          filesize,
          url,
        };

        setUploadedDocuments((prev) => [...prev, newDocument]);

        // console.log("ViewModel: Document uploaded successfully:", newDocument);

        // Return the FID to the caller
        return fid;
      } catch (err) {
        console.error("ViewModel: Upload error:", err);

        const message =
          err instanceof Error
            ? err.message
            : "Failed to upload document. Please try again.";

        setError(message);
        throw err;
      } finally {
        setIsUploading(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    []
  );

  const removeDocument = useCallback((fid: string) => {
    // console.log("ViewModel: Removing document with FID:", fid);
    setUploadedDocuments((prev) => prev.filter((doc) => doc.fid !== fid));
  }, []);

  return {
    fileInputRef,
    uploadedDocuments,
    onUploadClick,
    onFileChange,
    isUploading,
    error,
    removeDocument,
    loadExistingDocuments, // NEW: Export the new function
  };
};
